'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useMetrica } from 'next-yandex-metrica';
import { authHref } from '@/shared/lib/auth-return';
import {
	consentOfProcessingPersonalDataDocument,
	orderOfProcessingPersonalDataDocument,
	userAgreementDocument,
	ofertaDocument,
} from '@/shared/constants';
import type { CourseLeadMagnetV1, FreeInterviewStateV1 } from '@/shared/api';
import {
	beginPromoRegistration,
	confirmPromoRegistration,
	loginPromo,
	promoAccount,
	savePromo,
} from '@/features/interview-promo/actions';
import {
	promoCookie,
	promoCourseDestination,
	promoEvent,
	promoGrades,
	promoStep,
	restoreDraft,
	type PromoDraft,
} from '@/features/interview-promo/model';
import { PromoTelegram } from './telegram';
import { PromoMessage, PromoMessageContext } from './message';
import { PromoActions } from './actions';
import { Composer } from '../composer';
import { PinnedBar } from '../pinned-bar';
import { ChatActionButton } from '../chat-action-button';
import {
	ChatSurface,
	ChatScrollArea,
	ChatTranscript,
	ChatFooter,
} from '../chat-layout';

type Account = { id?: string; email: string } | null;
const legalDocuments = [
	orderOfProcessingPersonalDataDocument,
	userAgreementDocument,
	consentOfProcessingPersonalDataDocument,
	ofertaDocument,
];
const consentText = `Для продолжения, необходимо согласие с условиями [Политики обработки персональных данных](${orderOfProcessingPersonalDataDocument.url}) и [Пользовательского соглашения](${userAgreementDocument.url}), [согласие на обработку персональных данных](${consentOfProcessingPersonalDataDocument.url}), согласие с условиями [Оферты](${ofertaDocument.url}) и согласие на получение рекламных материалов`;
export function PromoRegistration({
	landing,
	entryCourseId,
}: {
	landing: CourseLeadMagnetV1;
	entryCourseId?: string;
}) {
	const config = landing;
	const initial = useRef({ landing, entryCourseId });
	const router = useRouter();
	const { reachGoal } = useMetrica();
	const analytics = useRef(reachGoal);
	analytics.current = reachGoal;
	const [draft, setDraft] = useState<PromoDraft>({ version: 2 });
	const [ready, setReady] = useState(false);
	const [resolvingCourse, setResolvingCourse] = useState(false);
	const [account, setAccount] = useState<Account>(null);
	const [authenticatedOnEntry, setAuthenticatedOnEntry] = useState(false);
	const animateMessages = useRef(false);
	const [state, setState] = useState<FreeInterviewStateV1 | null>(null);
	const [linked, setLinked] = useState(false),
		[busy, setBusy] = useState(false),
		[error, setError] = useState(''),
		[value, setValue] = useState('');
	const lock = useRef(false);
	const viewed = useRef('');
	const end = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const selected = config.courses.find((l) => l.courseId === draft.courseId);
	const courseId = selected?.courseId ?? '';
	const saved =
		!!state &&
		state.courseId === courseId &&
		!!state.questionnaire &&
		state.questionnaire?.languageId === selected?.languageId &&
		state.bonusGranted &&
		(authenticatedOnEntry ||
			state.questionnaire.targetGrade === draft.targetGrade);
	const step = promoStep(draft, !!account, saved, linked, !!state?.attemptId);
	const inputStep = ['email', 'code', 'password'].includes(step);
	const inputDisabled = !ready || !inputStep || busy || resolvingCourse;
	const track = useCallback(
		(event: string, extra: Record<string, string> = {}) =>
			promoEvent(analytics.current, event, {
				leadMagnetId: landing.id,
				courseId,
				step,
				source: 'landing',
				...extra,
			}),
		[courseId, landing.id, step],
	);
	const cookiePath = usePathname();
	const update = (next: Partial<PromoDraft>) => {
		setError('');
		setValue('');
		setDraft((d) => ({ ...d, ...next }));
	};
	const refresh = async (selectedCourse = courseId) => {
		const r = await promoAccount(selectedCourse);
		setAccount(r.user);
		setState(r.state);
		// Only a server/provider check can confirm a link; PromoTelegram does it again for this course.
		setLinked(false);
		if (r.user) {
			setDraft((d) => ({
				...d,
				owner: r.user!.id,
				email: r.user!.email,
				authMode: undefined,
				targetGrade:
					r.state?.questionnaire?.targetGrade ?? r.knownGrade ?? d.targetGrade,
			}));
		}
		return r;
	};
	useEffect(() => {
		const { landing: config, entryCourseId } = initial.current;

		let active = true;
		const init = async () => {
			let raw: string | undefined;
			try {
				raw = decodeURIComponent(
					document.cookie
						.split('; ')
						.find((c) => c.startsWith(`${promoCookie}=`))
						?.split('=')
						.slice(1)
						.join('=') ?? '',
				);
			} catch {}
			const d = restoreDraft(
				raw,
				config.courses.map((l) => l.courseId),
				entryCourseId,
			);
			const r = await promoAccount(d.courseId ?? '');
			if (!active) return;
			if (d.owner && d.owner !== r.user?.id) {
				delete d.targetGrade;
				delete d.consented;
				delete d.email;
				delete d.authMode;
			}
			setAccount(r.user);
			setAuthenticatedOnEntry(!!r.user);
			setState(r.state);
			if (r.user) {
				const ownDraft = d.owner === r.user.id;
				d.owner = r.user.id;
				d.email = r.user.email;
				d.authMode = undefined;
				d.targetGrade =
					r.state?.questionnaire?.targetGrade ??
					r.knownGrade ??
					(ownDraft ? d.targetGrade : undefined);
				// Подтверждённое ранее в этой форме согласие сохраняем при восстановлении
				// после входа; сам факт авторизации новым согласием не считается.
				d.consented =
					ownDraft && !r.state?.questionnaire && d.consented === true;
			}
			setDraft(d);
			setReady(true);
		};
		void init().catch(() => {
			if (active) setError('Не удалось загрузить диалог. Обнови страницу.');
		});
		return () => {
			active = false;
		};
	}, []);
	useEffect(() => {
		if (ready) animateMessages.current = true;
	}, [ready]);
	useEffect(() => {
		if (!ready) return;
		try {
			document.cookie = `${promoCookie}=${encodeURIComponent(JSON.stringify(draft))}; Path=${cookiePath}; Max-Age=2592000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
		} catch {}
	}, [draft, ready, cookiePath]);
	useEffect(() => {
		if (!ready || viewed.current === step) return;
		viewed.current = step;
		track('view');
		end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [ready, step, track]);
	const onLinked = useCallback(() => setLinked(true), []);
	const save = useCallback(async () => {
		if (lock.current) return;
		lock.current = true;
		setBusy(true);
		setError('');
		track('action', { action: 'save' });
		try {
			const r = await savePromo(courseId, {
				targetGrade: draft.targetGrade,
				languageId: selected!.languageId,
				...(draft.consented
					? {
							consentVersion: 'promo-v1' as const,
							dataConsent: true,
							marketingConsent: true,
						}
					: {}),
			});
			if (r.data) {
				setState(r.data);
				track('result', { action: 'save', outcome: 'success' });
			} else {
				setError(r.error);
				track('result', { action: 'save', outcome: 'error' });
			}
		} catch {
			setError('Нет соединения. Повтори сохранение ответов.');
			track('result', { action: 'save', outcome: 'network_error' });
		} finally {
			lock.current = false;
			setBusy(false);
		}
	}, [
		courseId,
		selected?.languageId,
		draft.targetGrade,
		draft.consented,
		authenticatedOnEntry,
		track,
	]);
	const saveRef = useRef(save);
	saveRef.current = save;
	useEffect(() => {
		if (ready && !resolvingCourse && step === 'save') {
			const t = setTimeout(() => void saveRef.current(), 0);
			return () => clearTimeout(t);
		}
	}, [ready, resolvingCourse, step]);
	const begin = async (email: string) => {
		const r = await beginPromoRegistration({
			courseId,
			email,
			dataConsent: true,
			marketingConsent: true,
			consentVersion: 'promo-v1',
		});
		if (r.data) {
			update({ email, authMode: r.data.mode });
			track('result', { action: 'email', outcome: r.data.mode });
		} else {
			setError(r.error);
			track('result', { action: 'email', outcome: 'error' });
		}
	};
	const submit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (inputDisabled || lock.current) return;
		lock.current = true;
		setBusy(true);
		setError('');
		track('action', { action: step });
		const input = value.trim();
		try {
			if (step === 'email') {
				await begin(input);
				return;
			}
			const result =
				step === 'password'
					? await loginPromo(draft.email ?? '', value)
					: await confirmPromoRegistration(input);
			setValue('');
			track('result', {
				action: step,
				outcome: result.success ? 'success' : 'error',
			});
			if (result.success) {
				await refresh();
			} else setError(result.error);
		} catch {
			setError('Нет соединения. Попробуй ещё раз.');
			track('result', { action: step, outcome: 'network_error' });
		} finally {
			lock.current = false;
			setBusy(false);
		}
	};
	const resend = async () => {
		if (lock.current) return;
		lock.current = true;
		setBusy(true);
		track('action', { action: 'resend' });
		try {
			await begin(draft.email ?? '');
		} catch {
			setError('Нет соединения. Попробуй отправить код ещё раз.');
		} finally {
			lock.current = false;
			setBusy(false);
		}
	};
	const chooseCourse = async (
		choice: CourseLeadMagnetV1['courses'][number],
	) => {
		if (resolvingCourse) return;
		setResolvingCourse(true);
		track('choice', {
			courseId: choice.courseId,
			language: String(choice.languageId),
		});
		setState(null);
		setLinked(false);
		update({ courseId: choice.courseId });
		try {
			if (account) await refresh(choice.courseId);
		} catch {
			setError('Не удалось загрузить данные курса. Обнови страницу.');
		} finally {
			setResolvingCourse(false);
		}
	};
	const inputLabel =
		step === 'email'
			? 'Email'
			: step === 'password'
				? 'Пароль'
				: step === 'code'
					? 'Код из письма'
					: 'Сообщение';
	const sendLabel =
		step === 'email'
			? 'Продолжить'
			: step === 'password'
				? 'Войти'
				: step === 'code'
					? 'Подтвердить'
					: 'Отправить';
	const placeholder = !ready
		? 'Загружаем диалог…'
		: !inputStep
			? 'Выбери действие в диалоге'
			: step === 'email'
				? 'you@example.com'
				: inputLabel;
	return (
		<main className="h-[100svh] overflow-hidden">
			<ChatSurface>
				<ChatScrollArea scrollRef={scrollRef}>
					<PinnedBar
						readOnly
						pinnedTitle={landing.title}
						pinnedLabel="К началу регистрации"
						onScrollToPinned={() => {
							track('action', { action: 'pinned_open' });
							scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
						}}
					/>
					<ChatTranscript>
						{!ready ? (
							<PromoMessage text={error || 'Загружаем диалог…'} />
						) : (
							<PromoMessageContext.Provider
								value={{
									animate: animateMessages.current || step === 'language',
									scrollRef,
								}}
							>
								<PromoMessage
									text={`Привет! Это AI симулятор алгоритмической секции в ${config.companyName}. Я задам вопросы с реального собеседования в ${config.companyName}. В конце ты получишь подробный отчет на какой грейд в ${config.companyIn} ты можешь расчитывать и возьмут ли тебя вообще.\n\nВыбери язык и направление, чтобы начать.`}
								>
									{step === 'language' && (
										<PromoActions>
											{config.courses.map((l) => (
												<ChatActionButton
													variant="white"
													key={l.courseId}
													onClick={() => void chooseCourse(l)}
												>
													{l.label}
												</ChatActionButton>
											))}
										</PromoActions>
									)}
								</PromoMessage>
								{draft.courseId && (
									<>
										<PromoMessage answer text={selected?.label ?? ''} />
										{!authenticatedOnEntry && (
											<PromoMessage text="Выбери грейд на который ты претендуешь">
												{step === 'grade' && (
													<PromoActions>
														{promoGrades.map((g) => (
															<ChatActionButton
																variant="white"
																key={g.id}
																onClick={() => {
																	track('choice', { grade: g.id });
																	update({ targetGrade: g.id });
																}}
															>
																{g.label}
															</ChatActionButton>
														))}
													</PromoActions>
												)}
											</PromoMessage>
										)}
									</>
								)}
								{!authenticatedOnEntry &&
									draft.courseId &&
									draft.targetGrade && (
										<>
											<PromoMessage
												answer
												text={
													promoGrades.find((g) => g.id === draft.targetGrade)
														?.label ?? ''
												}
											/>
											<div
												onClickCapture={(event) => {
													const href = (event.target as HTMLElement)
														.closest('a')
														?.getAttribute('href');
													const document = legalDocuments.find(
														(doc) => doc.url === href,
													);
													if (document)
														track('action', {
															action: 'privacy_open',
															document: document.id,
														});
												}}
											>
												<PromoMessage text={consentText} revealText={false}>
													{step === 'consent' && (
														<PromoActions className="mt-3.5">
															<ChatActionButton
																variant="white"
																onClick={() => {
																	track('action', { action: 'consent' });
																	update({ consented: true });
																}}
															>
																Согласен
															</ChatActionButton>
														</PromoActions>
													)}
												</PromoMessage>
											</div>
										</>
									)}
								{!authenticatedOnEntry && draft.courseId && draft.consented && (
									<>
										<PromoMessage answer text="Согласен" />
										<PromoMessage text="Напиши свой email. Мы пришлем туда финальный отчет с результатами собеседования." />
										{draft.email && <PromoMessage answer text={draft.email} />}
									</>
								)}
								{step === 'code' && (
									<PromoMessage text="Мы отправили код на почту. Введи его здесь, чтобы подтвердить email." />
								)}
								{step === 'password' && (
									<PromoMessage text="Этот email уже зарегистрирован. Введи пароль, чтобы продолжить." />
								)}
								{step === 'save' && (
									<PromoMessage text="Подготавливаем интервью…">
										{error && (
											<PromoActions>
												<ChatActionButton
													variant="white"
													disabled={busy}
													onClick={() => void save()}
												>
													Повторить сохранение
												</ChatActionButton>
											</PromoActions>
										)}
									</PromoMessage>
								)}
								{step === 'telegram' && state && (
									<PromoTelegram
										introduction={`Остался последний шаг перед стартом. Собеседование займет порядка 60 минут и за это время мы не сможем задать тебе все вопросы с собеседований в ${config.companyIn}. Но мы подготовили разбор ТОП-3 задач. Запусти бота, чтобы его получить.`}
										interviewId={state.interviewId}
										courseId={courseId}
										onLinked={onLinked}
										track={track}
									/>
								)}
								{step === 'ready' && (
									<PromoMessage
										text={
											state?.step === 'complete'
												? 'Это интервью уже пройдено. Отчёт придёт на email.'
												: state?.attemptId
													? 'Интервью уже начато. Можно продолжить.'
													: 'Telegram привязан. Можно начинать интервью.'
										}
									>
										<PromoActions>
											<ChatActionButton
												variant="white"
												onClick={() => {
													track('action', { action: 'open_course' });
													router.push(
														promoCourseDestination(courseId, state?.attemptId),
													);
												}}
											>
												{state?.attemptId ? 'Открыть курс' : 'Начать интервью'}
											</ChatActionButton>
										</PromoActions>
									</PromoMessage>
								)}
								{error && !inputStep && (
									<div role="alert">
										<PromoMessage text={error} />
									</div>
								)}
								<div ref={end} />
							</PromoMessageContext.Provider>
						)}
					</ChatTranscript>
				</ChatScrollArea>
				<ChatFooter>
					<form ref={formRef} onSubmit={submit}>
						<Composer
							key={step}
							value={value}
							onChange={setValue}
							onSend={() => formRef.current?.requestSubmit()}
							disabled={inputDisabled}
							sendLabel={sendLabel}
							placeholder={placeholder}
							inputProps={{
								id: 'promo-input',
								'aria-label': inputLabel,
								type:
									step === 'email'
										? 'email'
										: step === 'password'
											? 'password'
											: 'text',
								autoComplete:
									step === 'email'
										? 'email'
										: step === 'password'
											? 'current-password'
											: step === 'code'
												? 'one-time-code'
												: 'off',
								inputMode: step === 'code' ? 'numeric' : undefined,
								pattern: step === 'code' ? '[0-9]{6}' : undefined,
								maxLength: step === 'code' ? 6 : 254,
								required: inputStep,
								onInvalid: () =>
									track('result', {
										action: step,
										outcome: 'validation_error',
									}),
							}}
						/>
						{ready && inputStep && step !== 'email' && (
							<PromoActions className="mt-2.5">
								{step === 'password' && (
									<a
										className="flex items-center rounded-lg border border-gray-4 bg-white text-sm font-medium text-black transition-colors hover:bg-gray-5"
										href={authHref('/login/restore', cookiePath)}
										onClick={() =>
											track('action', { action: 'password_restore' })
										}
									>
										Забыли пароль?
									</a>
								)}
								{step === 'code' && (
									<ChatActionButton
										variant="white"
										disabled={busy}
										onClick={() => void resend()}
									>
										Отправить код повторно
									</ChatActionButton>
								)}
								<ChatActionButton
									variant="white"
									disabled={busy}
									onClick={() => {
										track('action', { action: 'change_email' });
										update({ authMode: undefined, email: undefined });
									}}
								>
									Изменить email
								</ChatActionButton>
							</PromoActions>
						)}
						{ready && inputStep && error && (
							<p role="alert" className="mt-2.5 text-sm text-red">
								{error}
							</p>
						)}
					</form>
				</ChatFooter>
			</ChatSurface>
		</main>
	);
}
